import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Spinner} from 'flowbite-react';
import {Drawer} from '../../../components/Modals';
import {Resource} from '../../Resource/types';
import CustomButton from '../../../components/common/CustomButton';
import NumberInput from '../../../components/common/NumberInput';
import MultiSelect from '../../../components/common/MultiSelect';
import {SingleSelect} from '../../../components/common/Search';
import PersonAvatar from './PersonAvatar';
import {useOrganizationService, useResourcesService} from '../../../services';
import {useJwt} from '../../../helpers/common';
import {setResources} from '../../../redux/slices/resources';
import toast from "react-hot-toast";
import {UserRole} from '../../../types/manageUsers';
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import {HiDownload, HiHome, HiCheckCircle} from 'react-icons/hi';
import { ConfirmationOverlay } from '../../../shared/modals/ConfirmationOverlay';

interface PeopleModalProps {
  isVisible: boolean;
  onClose: () => void;
  resource: Resource | null;
  onUpdate?: (updatedResource: Resource) => void;
}

// Optimized form configuration using data structure
const FORM_FIELDS = [
  { name: 'cost_per_hour', label: 'Cost Per Hour', min: 0, max: 10000, required: true },
  { name: 'billing_per_hour', label: 'Billing Per Hour', min: 0, max: 10000, required: true },
] as const;

const ROLE_OPTIONS = [
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.BASIC_MEMBER, label: 'Basic Member' },
];

// Helper function to normalize skill names (matching OrgSkillsModal logic)
const normalizeSkill = (s: string | undefined | null) => (s || '').toUpperCase().trim();

const PeopleModal: React.FC<PeopleModalProps> = ({
  isVisible,
  onClose,
  resource,
}) => {
  // Consolidated state
  const [formData, setFormData] = useState<any>({});
  const [selectedItems, setSelectedItems] = useState<{ skills: any[]; teams: any[] }>({ skills: [], teams: [] });
  const [showCostResyncMessage, setShowCostResyncMessage] = useState(false);
  const [showCacheClearWarning, setShowCacheClearWarning] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  const dispatch = useDispatch();
  const { updateResource, addTeamsToResource, removeTeamsFromResource, fetchManagerResources } = useResourcesService();
  const { fetchOrganizationSkill, resetTeamsFetchedFlag, deleteOrgCache } = useOrganizationService();

  const { loading } = useSelector((state: any) => state.resources);
  const { skills: skillsData, teams: teamsData, teamsFetched } = useSelector((state: any) => state.organization);
  const resourcesData = useSelector((state: any) => state.resources.data);
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;

  // Memoized options for O(1) efficiency
  const options = useMemo(() => {
    const createOptions = (data: any[], labelKey: string) =>
      [...(data || [])]
        .sort((a, b) => a[labelKey]?.toLowerCase().localeCompare(b[labelKey]?.toLowerCase()))
        .map(item => ({ value: item.id, label: item[labelKey] }));

    const teamOptions = [...(teamsData || [])]
      .sort((a, b) => (a?.name || '').toLowerCase().localeCompare((b?.name || '').toLowerCase()))
      .map(team => {
        const projectNamesFromArray = Array.isArray(team?.projects)
          ? team.projects
              .map((project: any) => project?.project_name)
              .filter((name: string | undefined) => typeof name === 'string' && name.trim().length > 0)
          : [];
        const projectNames = projectNamesFromArray.length > 0
          ? projectNamesFromArray
          : typeof team?.project_name === 'string' && team.project_name.trim().length > 0
            ? [team.project_name]
            : [];
        const projectLabel = projectNames.length > 0 ? ` (${projectNames.join(', ')})` : '';

        return {
          value: team.id,
          label: `${team?.name || 'Unnamed Team'}${projectLabel}`,
          rawName: team?.name || 'Unnamed Team',
          projectNames,
          primaryProjectName: projectNames[0] || ''
        };
      });

    return {
      skills: createOptions(skillsData, 'skill'),
      teams: teamOptions
    };
  }, [skillsData, teamsData]);

  // Find the default skill from skillsData
  const defaultSkill = useMemo(() => {
    if (!skillsData || skillsData.length === 0) return null;
    return skillsData.find((skill: any) => normalizeSkill(skill?.skill) === 'DEFAULT');
  }, [skillsData]);

  // Single initialization effect
  useEffect(() => {
    if (!resource || !isVisible) return;

    const { skills = [], teams = [], user_role, ...restData } = resource;
    const initialFormData = { ...restData, resource_name: resource.name, user_role: user_role || UserRole.BASIC_MEMBER };
    setFormData(initialFormData);

    // Efficient mapping using Set for O(1) lookup
    const skillIdSet = new Set(skills.map(String));
    const teamIdSet = new Set(teams.map((t: any) => t.id));

    let initialSkills = options.skills.filter(opt => skillIdSet.has(String(opt.value)));
    
    // Ensure default skill is always included
    if (defaultSkill) {
      const defaultSkillOption = options.skills.find(opt => String(opt.value) === String(defaultSkill.id));
      if (defaultSkillOption && !initialSkills.find(s => String(s.value) === String(defaultSkill.id))) {
        initialSkills = [defaultSkillOption, ...initialSkills];
      }
    }

    const initialSelectedItems = {
      skills: initialSkills,
      teams: options.teams.filter(opt => teamIdSet.has(opt.value))
    };

    setSelectedItems(initialSelectedItems);
  }, [resource, options, isVisible, defaultSkill]);

  // Reset resync state only when modal closes/reopens (not on resource prop updates from our own dispatch)
  useEffect(() => {
    if (!isVisible) {
      setShowCostResyncMessage(false);
      setShowCacheClearWarning(false);
      setIsClearingCache(false);
    }
  }, [isVisible]);

  // Data fetching optimization
  useEffect(() => {
    if (!org_id) return;
    if (!skillsData?.length) fetchOrganizationSkill(org_id);
  }, [org_id, skillsData?.length, teamsFetched]);

  const hasUnsavedChanges = useMemo(() => {
    if (!resource?.id || !isVisible) {
      return false;
    }
    // Wait for formData to be initialized from useEffect before comparing
    if (!formData?.id) {
      return false;
    }

    const { skills = [], teams = [], user_role, ...restData } = resource;
    const originalFormData = { ...restData, resource_name: resource.name, user_role: user_role || UserRole.BASIC_MEMBER };
    
    const skillIdSet = new Set(skills.map(String));
    const teamIdSet = new Set(teams.map((t: any) => t.id));

    // Reconstruct original skills with default skill included (same logic as init effect)
    let originalSkills = options.skills.filter(opt => skillIdSet.has(String(opt.value)));
    if (defaultSkill) {
      const defaultSkillOption = options.skills.find(opt => String(opt.value) === String(defaultSkill.id));
      if (defaultSkillOption && !originalSkills.find(s => String(s.value) === String(defaultSkill.id))) {
        originalSkills = [defaultSkillOption, ...originalSkills];
      }
    }

    const originalSelectedItems = {
      skills: originalSkills,
      teams: options.teams.filter(opt => teamIdSet.has(opt.value))
    };

    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    const selectedItemsChanged = JSON.stringify(selectedItems) !== JSON.stringify(originalSelectedItems);

    return formDataChanged || selectedItemsChanged;
  }, [resource, formData, selectedItems, options, isVisible]);

  // Detect if cost or billing changed from original resource
  const hasCostChanged = useMemo(() => {
    if (!resource?.id) return false;
    const costChanged = Number(formData?.cost_per_hour || 0) !== Number(resource?.cost_per_hour || 0);
    const billingChanged = Number(formData?.billing_per_hour || 0) !== Number(resource?.billing_per_hour || 0);
    return costChanged || billingChanged;
  }, [resource, formData]);

  // Optimized handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? '' : Math.max(0, Math.min(10000, Math.floor(Number(value))));
    setFormData((prev: any) => ({ ...prev, [name]: numValue }));
  }, []);

  const handleMultiSelectChange = useCallback((type: 'skills' | 'teams') =>
    (value: any) => {
      if (type === 'skills') {
        if (!value || value.length === 0) {
          toast.error('At least one skill is required.');
          return;
        }
        
        // Prevent removing the default skill
        if (defaultSkill) {
          const currentSkills = selectedItems.skills;
          const defaultSkillOption = options.skills.find(opt => String(opt.value) === String(defaultSkill.id));
          
          if (defaultSkillOption) {
            const wasDefaultSelected = currentSkills.some(s => String(s.value) === String(defaultSkill.id));
            const isDefaultRemoved = !value.some((v: any) => String(v.value) === String(defaultSkill.id));
            
            if (wasDefaultSelected && isDefaultRemoved) {
              toast.error('Default skill cannot be removed. Every resource must have the default skill.');
              return;
            }
            
            // Ensure default skill is always included
            const hasDefault = value.some((v: any) => String(v.value) === String(defaultSkill.id));
            if (!hasDefault) {
              value = [defaultSkillOption, ...value];
            }
          }
        }
      }
      setSelectedItems(prev => ({ ...prev, [type]: value }));
    }
  , [defaultSkill, selectedItems.skills, options.skills]);

  const handleRoleChange = useCallback((selectedOption: any) => {
    setFormData((prev: any) => ({ ...prev, user_role: selectedOption?.value || UserRole.BASIC_MEMBER }));
  }, []);

  // Efficient team diff using Sets
  const calculateTeamChanges = useCallback((currentTeams: any[], newTeamIds: number[]) => {
    const currentIds = new Set(currentTeams.map((t: any) => t.id));
    const newIds = new Set(newTeamIds);

    return {
      toAdd: newTeamIds.filter(id => !currentIds.has(id)),
      toRemove: currentTeams.map((t: any) => t.id).filter(id => !newIds.has(id))
    };
  }, []);

  const handleCacheClearConfirm = async () => {
    setIsClearingCache(true);
    try {
      await deleteOrgCache();
      setShowCacheClearWarning(false);
      setShowCostResyncMessage(false);
      onClose();
    } catch {
      // Error already handled by service
    } finally {
      setIsClearingCache(false);
    }
  };

  // Optimized submit with batch operations
  const handleSubmit = useCallback(async () => {
    if (loading || !formData.id) return;

    try {
      let skills = selectedItems.skills.map(s => String(s.value));
      if (skills.length === 0) {
        toast.error('Please select at least one skill before saving.');
        return;
      }
      
      // Ensure default skill is always included (safety check)
      if (defaultSkill) {
        const defaultSkillId = String(defaultSkill.id);
        if (!skills.includes(defaultSkillId)) {
          skills = [defaultSkillId, ...skills.filter(id => id !== defaultSkillId)];
        }
      }
      
      const teamIds = selectedItems.teams.map(t => t.value);

      const costDidChange = hasCostChanged;

      // Don't close if cost changed — we'll show the resync banner after API succeeds
      if (!costDidChange) {
        onClose();
      }

      await updateResource({ ...formData, skills, user_role: formData.user_role, org_id });

      // Show resync banner only AFTER API responds successfully
      if (costDidChange) {
        setShowCostResyncMessage(true);
      }

      const { toAdd, toRemove } = calculateTeamChanges(resource?.teams || [], teamIds);

      // Batch operations for team management
      const promises = [];
      
      // Add teams to resource in a single call if there are teams to add
      if (toAdd.length > 0) {
        promises.push(addTeamsToResource({ 
          team_id: toAdd, 
          resource_id: Number(formData.id) 
        }));
      }

      // Remove resource from all teams at once
      if (toRemove.length > 0) {
        promises.push(removeTeamsFromResource({ 
          team_id: toRemove, 
          resource_id: Number(formData.id) 
        }));
      }

      await Promise.all(promises);

      // Only refresh manager resources if cost didn't change.
      // fetchManagerResources dispatches setManageResourcesLoading(true) which causes
      // the parent (Resource/index.tsx) to unmount PeopleContent and show a skeleton,
      // destroying all local state including the modal visibility and resync banner.
      if (!costDidChange) {
        resetTeamsFetchedFlag();
        await fetchManagerResources(org_id);
      }

      // Efficient state update
      const updatedResources = resourcesData.map((r: any) => {
        if (r.id !== formData.id) return r;

        return {
          ...r, ...formData, skills, user_role: formData.user_role,
          skills_name: selectedItems.skills.map(s => s.label),
          teams: selectedItems.teams.map(t => ({ id: t.value, name: t.rawName ?? t.label,
            project_name: t.primaryProjectName,
            projects: (t.projectNames || []).map((projectName: string) => ({
              project_name: projectName
            }))
          }))
        };
      });

      dispatch(setResources(updatedResources));
    } catch (error) {
      toast.error('Failed to update resource. Please try again.');
    }
  }, [loading, formData, selectedItems, resource, calculateTeamChanges, updateResource, addTeamsToResource, removeTeamsFromResource, dispatch, resourcesData, org_id, onClose, defaultSkill, hasCostChanged]);

  if (!resource) return null;

  return (
    <Drawer isOpen={isVisible} onClose={onClose} position="right">
      <div className="h-screen flex flex-col bg-light-surface dark:bg-tertiary-dark">
        {/* Header */}
        <div className="flex-shrink-0 rounded-b-2xl shadow-md flex justify-between items-center border-b border-gray-400 dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-white">
              Edit Person
            </h2>
            <p className="text-xs text-light-text-secondary dark:text-white/70 mt-0.5">
              Update person details and assignments
            </p>
          </div>
          <div className="flex gap-3">

            <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {(loading && !showCostResyncMessage) ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cost Resync Message - at the top */}
              {showCostResyncMessage && (
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 overflow-hidden shadow-sm">
                  {/* Success message section */}
                  <div className="p-4 flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <HiCheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        Resource cost updated successfully!
                      </p>
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                        This change will reflect across all your work items within 24 hours during the next auto-sync.
                      </p>
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="border-t border-green-200 dark:border-green-800/50" />
                  {/* Action section */}
                  <div className="p-3 bg-green-100/50 dark:bg-green-900/30 flex items-center justify-between gap-3">
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Need changes immediately?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowCacheClearWarning(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Update Cost Across Work Items
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCostResyncMessage(false);
                          onClose();
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800/40 hover:bg-green-200 dark:hover:bg-green-800/60 rounded-md transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Member Profile Card */}
              <div className="bg-light-surface  dark:bg-[#1e283a] rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="flex items-center justify-between">
                  {/* Left - Avatar and Name */}
                  <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden shadow-sm">
                      <PersonAvatar
                        name={resource.name || "No Name"}
                        imageUrl={resource.image_url}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {resource.name || 'Unknown User'}
                        </h4>
                        {/* Source badge - Imported or Cadence */}
                        {resource.imported ? (
                          <div className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-blue-400 rounded-md text-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-400 shadow-sm">
                            <HiDownload className="w-3 h-3" />
                            <span>Imported</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-emerald-400 rounded-md text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-500 dark:text-emerald-400 shadow-sm">
                            <HiHome className="w-3 h-3" />
                            <span>Cadence</span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                        {/* <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300">
                          #{resource.id || 'N/A'}
                        </span>
                        <span className="mx-1">•</span> */}
                        <span className="">
                          {resource.email || 'No email'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right - Stats */}
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {selectedItems.skills?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Skills</div>
                    </div>

                    <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {selectedItems.teams?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Teams</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Role <span className="text-red-500">*</span>
                </label>
                <SingleSelect
                  key={`role-${resource?.id}-${formData.user_role}`}
                  name="user_role"
                  options={ROLE_OPTIONS}
                  placeholder="Select role"
                  onChange={handleRoleChange}
                  defaultValue={ROLE_OPTIONS.find(option => option.value === (formData.user_role || UserRole.BASIC_MEMBER))}
                  className="text-sm"
                  width="w-full"
                />
              </div>

              {/* Multi-selects */}
              <MultiSelect
                id="skills"
                name="skills"
                label="Skills"
                value={selectedItems.skills}
                onChange={handleMultiSelectChange('skills')}
                options={options.skills}
                placeholder="Select skills"
              />

              <MultiSelect
                id="teams"
                name="teams"
                label="Teams"
                value={selectedItems.teams}
                onChange={handleMultiSelectChange('teams')}
                options={options.teams}
                placeholder="Select teams"
              />

              {/* Number inputs using configuration */}
              <div className="grid grid-cols-2 gap-4">
                {FORM_FIELDS.map(field => (
                  <NumberInput
                    key={field.name}
                    id={field.name}
                    name={field.name}
                    label={field.label}
                    value={formData[field.name] || ''}
                    onChange={handleInputChange}
                    placeholder={field.label}
                    min={field.min}
                    max={field.max}
                    required={field.required}
                    showAsterisk={field.required}
                    disabled={showCostResyncMessage}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showCostResyncMessage && (
          <div className="flex-shrink-0 rounded-t-md flex justify-between items-center border-t border-gray-500 dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4">
            <CustomButton variant="cancel" onClick={onClose}>
              Cancel
            </CustomButton>
            <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} className="ml-auto mr-3" />
            <CustomButton onClick={handleSubmit} disabled={loading} isLoading={loading} className={hasUnsavedChanges ? '' : 'ml-auto'}>
              Submit
            </CustomButton>
          </div>
        )}
      </div>

      <ConfirmationOverlay
        isOpen={showCacheClearWarning}
        onCancel={() => setShowCacheClearWarning(false)}
        onConfirm={handleCacheClearConfirm}
        title="Refresh System for Work Items?"
        description="This will refresh the system for all your work items so the updated cost is applied immediately."
        secondaryDescription="After refreshing, pages may take a moment to load while the changes are applied across your work items."
        confirmText="Refresh & Apply"
        loadingText="Refreshing..."
        isLoading={isClearingCache}
      />
    </Drawer>
  );
};

export default PeopleModal; 